import {
  Document,
  pdf,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

import { Button } from "../components";
import { getReceiptPrintDate } from "../utility";
import {
  EMPTY_STRING,
  shopifyConstants,
  inventoryConstants,
  buttonNameConstants,
  buttonTypeConstants,
  SHOPIFY_OPTIONS_LIST,
} from "../../constants";

//------get option list for varients
export const getOptionsList = (fieldsMapping) => {
  return SHOPIFY_OPTIONS_LIST.map((option) => {
    const name = fieldsMapping[option.name];
    const value = fieldsMapping[option.value];
    if (name === undefined || value === undefined) return null;
    return { name, value };
  }).filter(Boolean);
};

// Reverse of getOptionsList: takes an options array and returns an object mapping option names/values to their values
export const getFieldsMappingFromOptions = (options) => {
  let fieldsMapping = {};
  options?.forEach((opt, idx) => {
    if (SHOPIFY_OPTIONS_LIST[idx]) {
      fieldsMapping[SHOPIFY_OPTIONS_LIST[idx].name] = opt.name;
      fieldsMapping[SHOPIFY_OPTIONS_LIST[idx].value] = opt.value;
    }
  });

  return fieldsMapping;
};

//-------get mapping fields to save
export const getMappedShopifyFields = (fieldsMapping) => {
  //------- Mapped the selected columns from csv file to object to save in db
  return {
    variantMapping: {
      sku: fieldsMapping.sku || "",
      compare_at_price: fieldsMapping.compare_at_price || "",
      grams: EMPTY_STRING,
      weight: fieldsMapping.weight || EMPTY_STRING,
      weight_unit: fieldsMapping.weight_unit || "",
      inventory_management: fieldsMapping.inventory_management || "shopify",
      inventory_policy: fieldsMapping.inventory_policy || "",
      fulfillment_service: EMPTY_STRING,
      requires_shipping: fieldsMapping.requires_shipping ?? true,
      taxable: fieldsMapping.taxable ?? true,
      inventory_quantity:
        fieldsMapping.inventory_quantity || inventoryConstants.AUTO_GENERATED,
      barcode: fieldsMapping.barcode || "",
      position: fieldsMapping.position || inventoryConstants.AUTO_GENERATED,
      price: fieldsMapping.price || inventoryConstants.AUTO_GENERATED,
    },
    shopifyTags: fieldsMapping.shopifyTags || [],
    body_html: fieldsMapping.body_html || "",
    created_at: fieldsMapping.created_at || "2025-05-29T10:24:31.833Z",
    handle: fieldsMapping.handle || inventoryConstants.AUTO_GENERATED,
    product_type: fieldsMapping.product_type || "",
    published_at:
      fieldsMapping.published_at || inventoryConstants.AUTO_GENERATED,
    template_suffix: fieldsMapping.template_suffix || "",
    title: fieldsMapping.title || "",
    updated_at: fieldsMapping.updated_at || "2025-05-29T10:24:31.833Z",
    vendor: fieldsMapping.vendor || "",
    status: fieldsMapping.status || inventoryConstants.AUTO_GENERATED,
    published_scope: fieldsMapping.published_scope || "global",
    tags: fieldsMapping.tags || "",
    published: fieldsMapping.published ?? true,
    image: fieldsMapping.image || inventoryConstants.AUTO_GENERATED,
    options: getOptionsList(fieldsMapping),
  };
};

export const getDefaultFields = (data) => {
  //------- mapped  the parse the db save mapping to show in UI in mapping modal
  if (data?.shopifyMapping) {
    const mapped = { ...data.shopifyMapping };
    delete mapped.variantMapping;
    const options = getFieldsMappingFromOptions(mapped.options);
    return { ...mapped, ...data.shopifyMapping.variantMapping, ...options };
  }
};

//------- Function to check if Shopify is connected
export const isShopifyConnected = (store) => !!store?.shopify?.accessToken;

//------- Function to check if Shopify is connected for product
export const isShopifyConnectedForProduct = (store, productIsLinkToShopify) =>
  isShopifyConnected(store) && productIsLinkToShopify;

//------- Function to check if Shopify is enabled for the store
export const isShopifyIntegrationEnabled = (store) =>
  store?.isShopifyIntegration || false;

export const handleCleanShopifyComboBoxCustomValue = (value) => {
  let updatedValue = value;
  if (
    updatedValue &&
    updatedValue.includes(shopifyConstants.COMBO_BOX_INPUT_VALUE_IDENTIFIER)
  ) {
    updatedValue = updatedValue
      .replaceAll(
        shopifyConstants.COMBO_BOX_INPUT_VALUE_IDENTIFIER,
        EMPTY_STRING
      )
      .trim();
  }
  return updatedValue;
};

//------- This function return total number of line items and sum of their quantities
export const getTotalLineProductsAndQuantity = (order) => ({
  totalLineProducts: order?.line_items?.length,
  totalQuantity: order?.line_items?.reduce(
    (sum, item) => sum + item.current_quantity,
    0
  ),
});

//-------get actions menu for orders
//------- This function returns an array of actions based on the order status and type

const getActionsMenu = (
  fulfillmentStatus,
  paymentStatus,
  orderType,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleTransferToLocation
) => {
  const { SHOPIFY_ORDER_STATUS, PICKUP, ONLINE } = shopifyConstants;
  const {
    MARK_AS_READY,
    MARK_AS_OPEN,
    MARK_AS_FULFILLED,
    TRANSFER_TO_LOCATION,
  } = buttonNameConstants;

  const terminalStatuses = [
    SHOPIFY_ORDER_STATUS.FULFILLED,
    SHOPIFY_ORDER_STATUS.CANCELLED,
  ];

  // Early exit if fulfillment OR payment is in a terminal state
  if (
    terminalStatuses.includes(fulfillmentStatus) ||
    terminalStatuses.includes(paymentStatus)
  ) {
    return [];
  }

  const actions = {
    ready: {
      functionRef: handleMarkOrderAsReady,
      buttonName: MARK_AS_READY,
    },
    open: {
      functionRef: handleMarkOrderAsOpen,
      buttonName: MARK_AS_OPEN,
    },
    fulfilled: {
      functionRef: handleMarkOrderAsFulfilled,
      buttonName: MARK_AS_FULFILLED,
    },
    needTransfer: {
      functionRef: handleTransferToLocation,
      buttonName: TRANSFER_TO_LOCATION,
    },
  };

  // if ([PICKUP, ONLINE].includes(orderType)) {
  if ([PICKUP].includes(orderType)) {
    switch (fulfillmentStatus) {
      case SHOPIFY_ORDER_STATUS.UNFULFILLED:
        return [actions.ready, actions.fulfilled];
      case SHOPIFY_ORDER_STATUS.READY:
        return [actions.open, actions.fulfilled];
      case SHOPIFY_ORDER_STATUS.NEED_TRANSFER:
        return [actions.needTransfer];
      default:
        return [];
    }
  }

  return [];
};

//------- Function to get Shopify order actions buttons
//------- This function returns a set of buttons based on the order status and type
export const getShopifyOrderActions = (
  order,
  currentStore,
  defaultGridState,
  updateShopifyOrderStatus,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleTransferToLocation
) => {
  const actions = getActionsMenu(
    order?.orderFulfillmentStatus,
    order?.orderPaymentStatus,
    order?.orderType,
    handleMarkOrderAsOpen,
    handleMarkOrderAsReady,
    handleMarkOrderAsFulfilled,
    handleTransferToLocation
  );

  return (
    <>
      {actions.map((action, idx) => (
        <Button
          key={idx}
          label={action.buttonName}
          buttonType={buttonTypeConstants.ACTION_BUTTON}
          handleClick={() =>
            action.functionRef(
              updateShopifyOrderStatus,
              currentStore,
              defaultGridState,
              order
            )
          }
        />
      ))}
    </>
  );
};

//------- Function to get order status as a span element with class
export const getShopifyOrderFulfillmentStatus = (orderStatus) => {
  const statusClasses = {
    [shopifyConstants.SHOPIFY_ORDER_STATUS.UNFULFILLED]:
      "order-status-unfulfilled",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.READY]: "order-status-ready",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.FULFILLED]: "order-status-fulfilled",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.CANCELLED]: "order-status-cancelled",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.PARTIAL_FULFILLED]:
      "order-status-partially-fulfilled",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.HOLD]: "order-status-hold",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.REFUNDED]: "order-status-refunded",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.PARTIAL_REFUNDED]:
      "order-status-partially-refunded",
    [shopifyConstants.SHOPIFY_ORDER_STATUS.NEED_TRANSFER]:
      "order-status-need-transfer",
  };

  return (
    <span
      className={`me-2 order-status-fulfilled ${
        statusClasses[orderStatus] || ""
      }`}
    >
      {orderStatus}
    </span>
  );
};

const createPackingSlip = (invoice, currentStore) => {
  //------- Define styles for the packing slip
  //------- Using React-PDF's StyleSheet to create styles
  const styles = StyleSheet.create({
    page: {
      padding: 20,
      fontSize: 12,
      fontFamily: "Helvetica",
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    title: { fontSize: 18, fontWeight: "bold" },
    addressSection: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    section: { marginBottom: 10 },
    tableHeader: {
      flexDirection: "row",
      fontWeight: "bold",
      marginVertical: 10,
    },
    tableRow: { flexDirection: "row", marginVertical: 5 },
    flex3: { flex: 3 },
    flex1Right: { flex: 1, textAlign: "right" },
    footer: {
      textAlign: "center",
      marginTop: 30,
    },
    line: {
      height: 1,
      backgroundColor: "#000", // black line
      marginVertical: 5, // space above and below
    },
  });

  //------- Design the packing slip layout
  return (
    <Page size="A4" style={styles.page} wrap>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>{currentStore.shopify.name}</Text>
        <View>
          <Text>Order {invoice.orderNumber}</Text>
          <Text>{invoice.date}</Text>
        </View>
      </View>

      {/* Address Section */}
      <View style={styles.addressSection}>
        <View>
          <Text>SHIP TO</Text>
          {invoice?.shipTo ? (
            invoice.shipTo.map((line, i) => <Text key={i}>{line}</Text>)
          ) : (
            <Text>No shipping address</Text>
          )}
        </View>
        <View>
          <Text>BILL TO</Text>
          {invoice.billTo.map((line, i) => (
            <Text key={i}>{line}</Text>
          ))}
        </View>
      </View>
      <View style={styles.line} />
      {/* Table Header */}
      <View style={styles.tableHeader}>
        <Text style={styles.flex3}>ITEMS</Text>
        <Text style={styles.flex1Right}>QUANTITY</Text>
      </View>

      {/* Items */}
      {invoice.items.map((item, i) => (
        <>
          <View key={i} style={styles.tableRow}>
            <Text style={styles.flex3}>{item.name}</Text>
            <Text style={styles.flex1Right}>{item.qty}</Text>
          </View>
          {item?.serialNumber?.length ? (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.flex3}>
                Serial Number(s) : {item.serialNumber.join(", ")}
              </Text>
            </View>
          ) : (
            ""
          )}
        </>
      ))}
      <View style={styles.line} />
      {invoice.isRefundedItems && (
        <>
          <Text>
            There are other items from your order not included in this shipment.
          </Text>
          <View style={styles.line} />
        </>
      )}
      {/* Footer */}
      <View style={styles.footer}>
        <Text>Thank you for shopping with us!</Text>
        <Text>{currentStore?.shopify?.name}</Text>
        <Text>
          {currentStore?.shopify?.locations[0].address.formatted.join(" ")}
        </Text>
        <Text>{currentStore?.shopify?.email}</Text>
        <Text>{currentStore?.shopify?.shop}</Text>
      </View>
    </Page>
  );
};

//------- Function to handle printing of Shopify packing slips
//------- create slips fro selected orders on each page
//------- and open print dialog
export const handlePrintShopifyPackingSlips = async (
  selectedRows,
  currentStore
) => {
  if (selectedRows?.length > 0) {
    const invoices = selectedRows.map((order) => {
      // Collect refunded variant IDs
      const refundedVariantIds = Array.isArray(order.refundedItems)
        ? order.refundedItems.map((item) => item.variant_id)
        : [];

      return {
        order: order,
        orderNumber: order?.name || "N/A",
        date: getReceiptPrintDate(order?.createdOn),
        shipTo: order?.shipping_address
          ? [
              order?.shipping_address?.name || "N/A",
              order?.shipping_address?.address1 || "N/A",
              order?.shipping_address?.city || "N/A",
              `${order?.shipping_address?.province || ""} ${
                order?.shipping_address?.zip || ""
              }` || "N/A",
              order?.shipping_address?.country || "N/A",
            ]
          : false,
        billTo: [
          order?.billing_address?.name || "N/A",
          order?.billing_address?.address1 || "N/A",
          order?.billing_address?.city || "N/A",
          `${order?.billing_address?.province || ""} ${
            order?.billing_address?.zip || ""
          }` || "N/A",
          order?.billing_address?.country || "N/A",
        ],
        // prettier-ignore
        // ignoring prettier to make sure the item title is readable
        items: order.line_items
          .filter((item) => item.current_quantity)
          .map((item) => ({
            name: `${item.title}\n${item?.vendor} (${item.variant_title}) ${item.lineItemStatus ? `(${item.lineItemStatus})` : EMPTY_STRING}\n${item?.isOnline ? "(Online)" : "(Collection/Online)"}`,
            qty: `${item.current_quantity}`,
            serialNumber: item?.serialNumbers || [],
          })),
        isRefundedItems: order.refundedItems?.length > 0,
      };
    });
    const doc = (
      <Document>
        {invoices.map((invoice, idx) =>
          createPackingSlip(invoice, currentStore)
        )}
      </Document>
    );

    const blob = await pdf(doc).toBlob();
    const blobURL = URL.createObjectURL(blob);
    const win = window.open(blobURL);
    win.print();
  }
};

//------- Function to get order status options for order detail status dropdown
export const getShopifyOrderFulfillmentStatusOptions = (order) => {
  const actions = getActionsMenu(
    order?.orderFulfillmentStatus,
    order?.orderPaymentStatus,
    order?.orderType,
    () => {},
    () => {},
    () => {},
    () => {}
  );
  return actions.map((action) => ({
    value: action.buttonName,
    label: action.buttonName,
  }));
};

export const handleTransferToLocation = (
  updateShopifyOrderStatus,
  currentStore,
  gridState,
  order
) => {
  if (order?.id) {
    updateShopifyOrderStatus(
      currentStore?.id,
      {
        id: order?.id,
        orderStatus: shopifyConstants.SHOPIFY_ORDER_STATUS.NEED_TRANSFER,
      },
      gridState,
      false
    );
  }
};

export const handleMarkOrderAsFulfilled = (
  updateShopifyOrderStatus,
  currentStore,
  gridState,
  order
) => {
  if (order?.id) {
    updateShopifyOrderStatus(
      currentStore?.id,
      {
        id: order?.id,
        orderStatus: shopifyConstants.SHOPIFY_ORDER_STATUS.FULFILLED,
      },
      gridState,
      false
    );
  }
};

export const handleMarkOrderAsReady = (
  updateShopifyOrderStatus,
  currentStore,
  gridState,
  order
) => {
  if (order?.id) {
    updateShopifyOrderStatus(
      currentStore?.id,
      {
        id: order?.id,
        orderStatus: shopifyConstants.SHOPIFY_ORDER_STATUS.READY,
      },
      gridState,
      false
    );
  }
};

export const handleMarkOrderAsOpen = (
  updateShopifyOrderStatus,
  currentStore,
  gridState,
  order,
  toggleModal = false
) => {
  if (order?.id) {
    updateShopifyOrderStatus(
      currentStore?.id,
      {
        id: order?.id,
        orderStatus: shopifyConstants.SHOPIFY_ORDER_STATUS.UNFULFILLED,
      },
      gridState,
      toggleModal
    );
  }
};

// returns either order is cancelled or not
export const handleIsShopifyOrderCancelled = (
  shopifyOrder,
  isReturnCssClass = false //returns strike through css class if order is cancelled
) => {
  let isCancelled = shopifyOrder?.cancelled_at;
  let res = isCancelled;
  if (isCancelled && isReturnCssClass) {
    res = "text-decoration-line-through";
  }
  return res;
};

export const isReinstallShopifyApp = (
  currentStore,
  isActualUser,
  isConnected
) => {
  const isReinstallShopifyAppTemp = isActualUser // reinstall flow is only supported for actual users, not the shopify testers
    ? currentStore?.shopify?.isReinstallShopifyApp && isConnected
    : false;
  return isReinstallShopifyAppTemp;
};

export const getShopifyConnectionLabel = (
  currentStore,
  isActualUser,
  isConnecting
) => {
  const isConnected = isShopifyConnected(currentStore);
  if (isConnecting) {
    return buttonNameConstants.CONNECTING;
  } else if (isReinstallShopifyApp(currentStore, isActualUser, isConnected)) {
    return buttonNameConstants.UPDATE + ` (New Version Available)`;
  } else if (isConnected) {
    return buttonNameConstants.CONNECTED;
  } else {
    return buttonNameConstants.CONNECT;
  }
};

export const isShopifyConnectionButtonDisabled = (
  currentStore,
  isConnecting,
  isActualUser
) => {
  const isConnected = isShopifyConnected(currentStore);

  if (isConnecting) {
    return true;
  } else if (!isConnected) {
    return false;
  } else if (isReinstallShopifyApp(currentStore, isActualUser, isConnected)) {
    return false;
  } else if (isConnected) {
    return true;
  } else {
    return false;
  }
};
